// src/utils/loadAccess.js
//
// Who may see a load. One rule, used by every page that lists loads — Current
// Loads, Accounting and the Dashboard — so a person sees the same work
// everywhere.
//
//   Super Admin              every load.
//   Accountant, HR, Fleet    every load of their companies, whatever the team —
//   (and nothing else)       only when that is their whole job. Most accounts
//                            give dispatchers and admins the Accountant role as
//                            well; they follow their team like everyone else.
//   On a team                their team's loads, and loads that belong to no
//                            team, in their companies.
//   On no team (and never    every load of their companies: the company does
//   taken off one)           not use teams for them.
//   Taken off a team         nothing of that team's — except their own loads
//                            (dispatching, or booked with no dispatcher yet), booked before they
//                            left and not yet finished, if the Super Admin
//                            allowed it when removing them ('manage' or 'view').
//                            A Main Admin ran the whole team, so for them it
//                            is all of the team's unfinished loads from before.
//   A load they dispatch     stays theirs to finish, on their own teams.
//   Driver granted by a      that driver's loads, wherever they are.
//   Super Admin
//   Moved from their team    a driver moved to another team took their
//                            unfinished loads along; the old team still sees
//                            them, read-only, until they are finished.
//
// What this replaced: "a load I dispatch is always mine" with no exception, and
// "my linked driver's loads are always mine". Both let a person taken off a
// team keep receiving that team's new work, because a new load copies the
// driver's dispatcher.
//
// The load's team comes from utils/loadTeam (stamped teamId, else its
// dispatcher's, driver's or creator's team), so callers pass the same lookups
// that deriveLoadTeamId takes.

import { deriveLoadTeamId, getTeamAccess, loadTeamRelation, driverOwnTeam } from './loadTeam';
import { getGrantedDriverIds } from './companyScope';

export const COMPANY_WIDE_ROLES = ['Accountant', 'HR', 'Fleet'];
const TEAM_ROLES = ['Main Admin', 'Admin', 'Dispatcher'];
const FINISHED_STATUSES = ['Delivered', 'Cancelled'];

const toMillis = (v) => {
  if (!v) return null;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
};

const rolesOf = (user) => (Array.isArray(user?.role) ? user.role : [user?.role].filter(Boolean));

/** Whether the viewer was that team's Main Admin when they left it. */
function leftAsMainAdmin(access, teamKey, viewerIsMainAdmin) {
  const recorded = access.formerAsMainAdmin.get(teamKey);
  // Recorded before this was tracked: go by their role now.
  return typeof recorded === 'boolean' ? recorded : viewerIsMainAdmin;
}

/**
 * For a load on a team this viewer has left: what they may still do with it.
 * 'manage' or 'view' only for a load booked before they left and not finished
 * — their own (one they dispatch, or booked themselves with no dispatcher on
 * it), or any of the team's if they were its Main Admin — and only as the
 * Super Admin allowed. Otherwise 'none'. Null when the load is not on a team
 * they left.
 */
function leftTeamLevel(load, teamKey, access, viewerId, viewerIsMainAdmin) {
  const leftAt = access.former.get(teamKey);
  if (leftAt == null) return null;
  // Handed to someone else when they left (utils/loadHandover): still theirs
  // to see, as the Super Admin chose.
  const own = Boolean(viewerId) && (load.dispatcherId === viewerId ||
    (!load.dispatcherId && load.createdBy === viewerId) ||
    (Array.isArray(load.dispatcherHandovers) &&
      load.dispatcherHandovers.some(h => h?.fromDispatcherId === viewerId)));
  if (!own && !leftAsMainAdmin(access, teamKey, viewerIsMainAdmin)) return 'none';
  const created = toMillis(load.createdAt);
  if (created != null && created >= leftAt) return 'none';
  if (FINISHED_STATUSES.includes(load.status)) return 'none';
  return access.formerAccess.get(teamKey) || 'view';
}

/**
 * True when this load was moved off one of the viewer's teams with its driver
 * (useTeamActions writes teamMoves) and is not finished. The old team keeps
 * sight of it, read-only, as work now managed by the new team.
 */
function movedFromMyTeam(load, teamKey, access) {
  if (FINISHED_STATUSES.includes(load.status)) return false;
  if (!Array.isArray(load.teamMoves) || access.teams.includes(teamKey)) return false;
  return load.teamMoves.some(m => m && access.teams.includes(m.fromTeamId));
}

/**
 * Build the check for one viewer. Returns a function (load) => boolean.
 *
 * `usersById` and `driversById` are Maps, as built for deriveLoadTeamId. Users
 * must carry teamIds or old, unstamped loads cannot be placed on a team.
 */
export function makeLoadAccess(viewer, { usersById = new Map(), driversById = new Map() } = {}) {
  if (!viewer) return () => false;

  const roles = rolesOf(viewer);
  if (roles.includes('Super Admin')) return () => true;

  // No company field at all is an old account that was never scoped; it keeps
  // seeing its whole tenant. An empty list is "assigned to nothing" — nothing.
  const companies = Array.isArray(viewer.assignedParentCompanyIds) ? viewer.assignedParentCompanyIds : null;
  const companyWide = roles.some(r => COMPANY_WIDE_ROLES.includes(r)) &&
    !roles.some(r => TEAM_ROLES.includes(r));
  const access = getTeamAccess(viewer);
  const granted = getGrantedDriverIds(viewer);
  const viewerId = viewer.uid || viewer.id || null;
  const viewerIsMainAdmin = roles.includes('Main Admin');

  const inMyCompanies = (load) => {
    if (!companies) return true;
    if (companies.length === 0) return false;
    // Old loads were never tagged with a company; they stay visible to the
    // tenant as they always have been.
    return !load.parentCompanyId || companies.includes(load.parentCompanyId);
  };

  return (load) => {
    if (!load) return false;
    if (load.driverId && granted.includes(load.driverId)) return true;

    const teamKey = deriveLoadTeamId(load, usersById, driversById);

    // A team they left. Checked before the company: someone moved to another
    // company is in the same position.
    const left = leftTeamLevel(load, teamKey, access, viewerId, viewerIsMainAdmin);
    if (left) return left !== 'none';

    // A load they dispatch is theirs to finish wherever it is filed.
    if (viewerId && load.dispatcherId === viewerId) return true;

    // Moved off one of their teams with its driver — to another company too,
    // when driver and dispatcher moved together (utils/loadHandover) — so
    // before the company check. Read-only, until delivered.
    if (movedFromMyTeam(load, teamKey, access)) return true;
    if (!inMyCompanies(load)) return false;
    if (companyWide || !access.usesTeams) return true;
    const relation = loadTeamRelation(load, teamKey, access);
    return relation === 'own' || relation === 'none';
  };
}

/**
 * For the loads a viewer sees: 'manage' or 'view' when the load is still theirs
 * only because of a team they left (the row then says so, and with 'view' is
 * read-only); null for every other load. Same lookups as makeLoadAccess.
 */
export function makeLeftTeamCheck(viewer, { usersById = new Map(), driversById = new Map() } = {}) {
  if (!viewer || rolesOf(viewer).includes('Super Admin')) return () => null;
  const access = getTeamAccess(viewer);
  if (access.former.size === 0) return () => null;
  const viewerId = viewer.uid || viewer.id || null;
  const viewerIsMainAdmin = rolesOf(viewer).includes('Main Admin');
  return (load) => {
    const level = leftTeamLevel(load, deriveLoadTeamId(load, usersById, driversById), access, viewerId, viewerIsMainAdmin);
    return level && level !== 'none' ? level : null;
  };
}

/** Whether the viewer left this team as its Main Admin (for the row's wording). */
export function leftTeamAsMainAdmin(viewer, teamKey) {
  if (!viewer || !teamKey) return false;
  return Boolean(leftAsMainAdmin(getTeamAccess(viewer), teamKey, rolesOf(viewer).includes('Main Admin')));
}

/**
 * Which drivers this viewer may PUT on a load — the same boundary as the loads.
 *
 * Returns (driver) => boolean, applied to a list that is already scoped to the
 * viewer's companies. Someone on a team picks only their team's drivers, plus
 * any driver a Super Admin granted them. A driver on no team is placed on a team
 * first. Super Admin,
 * company-wide roles and people on no team keep the whole list. Someone taken
 * off every team picks nobody.
 *
 * Without this a Main Admin could put another team's driver on a load while the
 * dispatcher list beside it offered only their own team.
 */
export function makeDriverPickAccess(viewer) {
  if (!viewer) return () => false;
  const roles = rolesOf(viewer);
  if (roles.includes('Super Admin')) return () => true;

  const companyWide = roles.some(r => COMPANY_WIDE_ROLES.includes(r)) &&
    !roles.some(r => TEAM_ROLES.includes(r));
  const access = getTeamAccess(viewer);
  if (companyWide || !access.usesTeams) return () => true;

  const granted = getGrantedDriverIds(viewer);
  return (driver) => {
    if (!driver) return false;
    if (granted.includes(driver.id)) return true;
    const team = driverOwnTeam(driver);
    return Boolean(team) && access.teams.includes(team);
  };
}

/**
 * For the loads a viewer sees: the id of the team a load was moved to, when it
 * is visible only because it was moved off the viewer's team with its driver.
 * The row then says so and is read-only. Null for every other load.
 */
export function makeMovedTeamCheck(viewer, { usersById = new Map(), driversById = new Map() } = {}) {
  if (!viewer || rolesOf(viewer).includes('Super Admin')) return () => null;
  const access = getTeamAccess(viewer);
  if (access.teams.length === 0) return () => null;
  return (load) => {
    if (!Array.isArray(load?.teamMoves)) return null;
    const teamKey = deriveLoadTeamId(load, usersById, driversById);
    return movedFromMyTeam(load, teamKey, access) ? teamKey : null;
  };
}
